import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Nav, Navbar } from "react-bootstrap";
import { HashLink } from "react-router-hash-link";
import { motion, useReducedMotion } from "framer-motion";
import "../../styles/HeaderStyle.css";
import ContactModal from "./ContactModal";

const Header = () => {
  const reduceMotion = useReducedMotion();

  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("#Home");
  const [showContactModal, setShowContactModal] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const sections = useMemo(() => ["#Home", "#features", "#download", "#contact"], []);
  const rafRef = useRef(null);

  const closeMenu = useCallback(() => setExpanded(false), []);
  const handleShowModal = useCallback(() => setShowContactModal(true), []);
  const handleCloseModal = useCallback(() => setShowContactModal(false), []);

  useEffect(() => {
    const computeActive = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setScrolled(y > 10);

      // pick last section whose top is above a buffer
      const buffer = 140;
      let current = "#Home";

      for (const hash of sections) {
        const id = hash.replace("#", "");
        const el = document.getElementById(id);
        if (!el) continue;

        const top = el.getBoundingClientRect().top;
        if (top - buffer <= 0) current = hash;
      }

      setActive((prev) => (prev === current ? prev : current));
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        computeActive();
      });
    };

    computeActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [sections]);

  const navLinkProps = (hash) => ({
    as: HashLink,
    smooth: true,
    to: hash,
    onClick: closeMenu,
    className: `qc-link ${active === hash ? "is-active" : ""}`,
  });

  return (
    <header className="qc-header">
      <Navbar
        collapseOnSelect
        expand="lg"
        expanded={expanded}
        onToggle={(v) => setExpanded(v)}
        className={`qc-navbar ${scrolled ? "is-scrolled" : ""}`}
      >
        <Container className="qc-wrap">
          <Navbar.Brand href="#Home" onClick={() => setActive("#Home")} className="qc-brand">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: -6 }}
              animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="qc-brandInner"
            >
              <span className="qc-logoText">
                <span className="qc-qwik">QWIK</span>
                <span className="qc-chow">CHOW</span>
              </span>
            
            </motion.div>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="qc-nav" className="qc-toggle" />

          <Navbar.Collapse id="qc-nav" className="qc-collapse">
            <Nav className="ms-auto align-items-lg-center qc-nav">
              <Nav.Link {...navLinkProps("#Home")}>Home</Nav.Link>
              <Nav.Link {...navLinkProps("#features")}>Features</Nav.Link>
              <Nav.Link {...navLinkProps("#download")}>Download</Nav.Link>

              <motion.button
                type="button"
                className="qc-cta"
                onClick={() => {
                  closeMenu();
                  handleShowModal();
                }}
                whileHover={reduceMotion ? {} : { y: -1, scale: 1.02 }}
                whileTap={reduceMotion ? {} : { scale: 0.98 }}
                transition={{ duration: 0.16 }}
              >
                Contact
              </motion.button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <ContactModal show={showContactModal} handleClose={handleCloseModal} />
    </header>
  );
};

export default Header;
